/**
 * Shared preview constants for mock data.
 * This file must be server-safe (no browser APIs, no React hooks).
 * Used by both client-side previewutils.tsx and server-side mockPreviewData.ts
 */

import { badge33, badge43 } from './badge';

export { badge33, badge43 };

export const PREVIEW_MAX_SIZE = 600;

export function getPreviewScaledSize(width: number, height: number) {
  const scaleX = width > PREVIEW_MAX_SIZE ? PREVIEW_MAX_SIZE / width : 1;
  const scaleY = height > PREVIEW_MAX_SIZE ? PREVIEW_MAX_SIZE / height : 1;
  const scale = Math.min(scaleX, scaleY);
  return {
    width: width * scale,
    height: height * scale,
    scale,
  };
}

/**
 * Get preview date (33 hours from now).
 * Function instead of const so it's fresh on each call.
 */
export const getPreviewDate = () => new Date(Date.now() + 33 * 60 * 60 * 1000);

/**
 * Mock data values for preview rendering.
 * Keys match the data point names used in templates.
 *
 * NOTE: Date fields (DAY, DATE, TIME, etc.) are NOT included here
 * because they need special handling (Date objects vs formatted strings).
 * Each consumer handles dates appropriately for their context.
 */
export const MockPreviewValues = {
  // Competition & Event
  COMPETITION_NAME: 'La Liga',
  EVENT_NAME: 'Some team vs Another',
  SPORT_NAME: 'Football',
  SPORT: 'Football',
  SPORT_ID: '1',
  VENUE: 'WWK Arena',
  VENUE_ID: '321',
  CITY_NAME: 'Augsburg',

  // Team Names
  HOME_NAME: 'Home Team',
  AWAY_NAME: 'Away Team',
  HOME_NAME_MICRO: 'HOM',
  AWAY_NAME_MICRO: 'AWA',
  COMPETITOR_NAME: 'Competitor',
  COMPETITOR_MICRO_NAME: 'COM',
  OPPONENT_NAME: 'Opponent',
  OPPONENT_MICRO_NAME: 'OPP',

  // Text dates (pre-formatted)
  TEXT_DATE: '19:00 tonight',
  TEXT_DATE_12: '07:00 PM tonight',

  // Odds (as tuples [numerator, denominator])
  ODDS: [10, 1] as [number, number],
  WAS_ODDS: [5, 1] as [number, number],
  HOME_ODDS: [12, 2] as [number, number],
  AWAY_ODDS: [5, 3] as [number, number],
  DRAW_ODDS: [20, 1] as [number, number],
  COMPETITOR_ODDS: [10, 1] as [number, number],
  OPPONENT_ODDS: [5, 1] as [number, number],
  OVER_ODDS: [10, 11] as [number, number],
  UNDER_ODDS: [10, 11] as [number, number],
  PROMO_ODDS: [10, 1] as [number, number],
  PROMO_WAS_ODDS: [5, 1] as [number, number],

  // Market
  MARKET_NAME: '90 Minutes',
  INSIGHT_TEXT: 'Insightful insight',
  PROMO_TEXT:
    'The next person to score will also be the next person to get sent off for headbutting the Ref.',

  // Spreads & Totals
  TOTAL: '47.5',
  HOME_SPREAD: '+2.5',
  AWAY_SPREAD: '-2.5',

  // Percentages
  HOME_PERCENT_BETS: '71%',
  HOME_PERCENT_MONEY: '69%',
  AWAY_PERCENT_BETS: '17%',
  AWAY_PERCENT_MONEY: '18%',
  DRAW_PERCENT_BETS: '12%',
  DRAW_PERCENT_MONEY: '13%',

  // Stake
  STAKE: '£10',

  // IDs
  COMPETITOR_ID: '23',
  FOCUS_COMPETITOR_ID: '',
  OPPONENT_COMPETITOR_ID: '',
  HOME_COMPETITOR_ID: '',
  AWAY_COMPETITOR_ID: '',
  FOCUS_COMPETITOR_COUNTRY_CODE: '',
  OPPONENT_COMPETITOR_COUNTRY_CODE: '',
  HOME_COMPETITOR_COUNTRY_CODE: '',
  AWAY_COMPETITOR_COUNTRY_CODE: '',
  COMPETITION_ID: '17',

  // Colors
  HOME_COLOR_PRIMARY: '#224076',
  AWAY_COLOR_PRIMARY: '#D62D2E',
  HOME_COLOR_SECONDARY: '#FFFFFF',
  AWAY_COLOR_SECONDARY: '#FFFFFF',

  // Images
  LANDSCAPE_IMAGE: 'https://example.com/landscape.jpg',
  SQUARE_IMAGE: 'https://example.com/square.jpg',
  PORTRAIT_IMAGE: 'https://example.com/portrait.jpg',

  // Options (for multi-option markets like SGP, Competition Winner)
  OPTION_1_ODDS: [1, 1] as [number, number],
  OPTION_2_ODDS: [2, 1] as [number, number],
  OPTION_3_ODDS: [3, 1] as [number, number],
  OPTION_4_ODDS: [4, 1] as [number, number],
  OPTION_5_ODDS: [5, 1] as [number, number],
  OPTION_6_ODDS: [6, 1] as [number, number],
  OPTION_7_ODDS: [7, 1] as [number, number],
  OPTION_8_ODDS: [8, 1] as [number, number],
  OPTION_9_ODDS: [9, 1] as [number, number],
  OPTION_10_ODDS: [10, 1] as [number, number],
  OPTION_1_NAME: 'Option 1',
  OPTION_2_NAME: 'Option 2',
  OPTION_3_NAME: 'Option 3',
  OPTION_4_NAME: 'Option 4',
  OPTION_5_NAME: 'Option 5',
  OPTION_6_NAME: 'Option 6',
  OPTION_7_NAME: 'Option 7',
  OPTION_8_NAME: 'Option 8',
  OPTION_9_NAME: 'Option 9',
  OPTION_10_NAME: 'Option 10',
  OPTION_1_COMPETITOR_ID: '1',
  OPTION_2_COMPETITOR_ID: '2',
  OPTION_3_COMPETITOR_ID: '3',
  OPTION_4_COMPETITOR_ID: '4',
  OPTION_5_COMPETITOR_ID: '5',
  OPTION_6_COMPETITOR_ID: '6',
  OPTION_7_COMPETITOR_ID: '7',
  OPTION_8_COMPETITOR_ID: '8',
  OPTION_9_COMPETITOR_ID: '9',
  OPTION_10_COMPETITOR_ID: '10',
  OPTION_1_OUTCOME_NAME: 'Outcome 1',
  OPTION_2_OUTCOME_NAME: 'Outcome 2',
  OPTION_3_OUTCOME_NAME: 'Outcome 3',
  OPTION_4_OUTCOME_NAME: 'Outcome 4',
  OPTION_5_OUTCOME_NAME: 'Outcome 5',
  OPTION_1_MARKET_NAME: 'Market 1',
  OPTION_2_MARKET_NAME: 'Market 2',
  OPTION_3_MARKET_NAME: 'Market 3',
  OPTION_4_MARKET_NAME: 'Market 4',
  OPTION_5_MARKET_NAME: 'Market 5',

  // NASCAR specific
  OPTION_1_GRID_POSITION: '1',
  OPTION_1_DRIVER_NUMBER: '#1',
  OPTION_2_GRID_POSITION: '2',
  OPTION_2_DRIVER_NUMBER: '#2',
  OPTION_3_GRID_POSITION: '3',
  OPTION_3_DRIVER_NUMBER: '#3',
  OPTION_4_GRID_POSITION: '4',
  OPTION_4_DRIVER_NUMBER: '#4',
  OPTION_5_GRID_POSITION: '5',
  OPTION_5_DRIVER_NUMBER: '#5',
  RACE_NAME: 'Race Name',
  SOMETHING: 'Something',
  SOMETHING_ELSE: 'Something else',

  // NBA Common
  TEAM_HOME_NAME: 'Team Home Name',
  TEAM_AWAY_NAME: 'Team Away Name',
  TEAM_HOME_NAME_SHORT: 'THN',
  TEAM_AWAY_NAME_SHORT: 'TAN',
  TEAM_HOME_NAME_FULL: 'Team Home Name Full',
  TEAM_AWAY_NAME_FULL: 'Team Away Name Full',

  // NBA Pre-event
  PRE_TEAM_HOME_WINS: '21',
  PRE_TEAM_HOME_LOSSES: '18',
  PRE_TEAM_AWAY_WINS: '20',
  PRE_TEAM_AWAY_LOSSES: '19',

  // NBA Live
  LIVE_FULL_SCORE: '123 - 98',
  LIVE_TEAM_HOME_SCORE: '123',
  LIVE_TEAM_AWAY_SCORE: '98',
  LIVE_TOP_POINTS_PLAYER_NAME: 'Player Name',
  LIVE_TOP_POINTS_PLAYER_TEAM: 'Team Name',
  LIVE_TOP_POINTS_PLAYER_COUNT: '45',

  // NBA Post-event
  POST_FULL_SCORE: '142 - 133',
  POST_TEAM_HOME_SCORE: '142',
  POST_TEAM_AWAY_SCORE: '133',
  POST_TOP_POINTS_PLAYER_NAME: 'Player Name',
  POST_TOP_POINTS_PLAYER_TEAM: 'Team name',
  POST_TOP_POINTS_PLAYER_COUNT: '66',

  //Player Props
  HOME_POINTS_NAME: 'Home Player',
  HOME_POINTS_TOTAL: '13.5',
  AWAY_POINTS_NAME: 'Away Player',
  AWAY_POINTS_TOTAL: '22.5',

  // Outright
  OUTRIGHT_NAME: 'Outright',

  // Video
  MAIN_VIDEO: undefined,
} as const;

/**
 * Team asset display data for HOME and AWAY teams.
 */
export const MockTeamAssets = {
  HOME_ASSET: {
    JERSEY: {
      type: 'kit' as const,
      data: {
        kind: 'shortSleeveJersey' as const,
        colours: {
          sleeve: '#D62D2E',
          background: '#224076',
        },
      },
    },
    BADGE: {
      type: 'badge' as const,
      data: {
        content: badge33,
      },
    },
    CUSTOM: {
      type: 'kit' as const,
      data: {
        kind: 'shortSleeveJersey' as const,
        colours: {
          sleeve: '#D62D2E',
          background: '#224076',
        },
      },
    },
  },
  AWAY_ASSET: {
    JERSEY: {
      type: 'kit' as const,
      data: {
        kind: 'shortSleeveJersey' as const,
        colours: {
          sleeve: '#224076',
          background: '#D62D2E',
        },
      },
    },
    BADGE: {
      type: 'badge' as const,
      data: {
        content: badge43,
      },
    },
    CUSTOM: {
      type: 'kit' as const,
      data: {
        kind: 'shortSleeveJersey' as const,
        colours: {
          sleeve: '#224076',
          background: '#D62D2E',
        },
      },
    },
  },
};

/**
 * Vector color palettes for preview.
 */
export const MockVectorPalettes = [
  {
    HOME_COLOR: { PRIMARY: 'red', SECONDARY: 'green' },
    AWAY_COLOR: { PRIMARY: 'blue', SECONDARY: 'cyan' },
    COMPETITOR_COLOR: { PRIMARY: 'yellow', SECONDARY: 'pink' },
    OPPONENT_COLOR: { PRIMARY: 'white', SECONDARY: 'purple' },
  },
  {
    HOME_COLOR: { PRIMARY: 'magenta', SECONDARY: 'yellow' },
    AWAY_COLOR: { PRIMARY: 'black', SECONDARY: 'purple' },
    COMPETITOR_COLOR: { PRIMARY: 'yellow', SECONDARY: 'pink' },
    OPPONENT_COLOR: { PRIMARY: 'white', SECONDARY: 'purple' },
  },
];

/**
 * Date-related data point keys.
 */
export const DATE_DATA_POINTS = [
  'DAY',
  'DATE',
  'MONTH',
  'TIME',
  'COUNTDOWN',
  'PRE_COUNTDOWN',
] as const;

export type DateDataPoint = (typeof DATE_DATA_POINTS)[number];

export function isDateDataPoint(key: string): key is DateDataPoint {
  return DATE_DATA_POINTS.includes(key as DateDataPoint);
}
